export interface ErrorReporterDeps {
  readonly onFatal: (info: ErrorInfo) => void;
  readonly target?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
}

export interface ErrorInfo {
  readonly kind: 'error' | 'unhandledrejection';
  readonly message: string;
  readonly stack?: string;
  readonly source?: string;
  readonly timestamp: number;
}

export interface ErrorReporter {
  dispose(): void;
}

function stringifyReason(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  try {
    return JSON.stringify(reason);
  } catch {
    return String(reason);
  }
}

export function createErrorReporter(deps: ErrorReporterDeps): ErrorReporter {
  const { onFatal } = deps;
  const target = deps.target ?? globalThis.window;
  let disposed = false;

  if (!target)
    throw new Error(
      'createErrorReporter: no target provided and globalThis.window is not defined (non-browser environment)',
    );

  function handleError(event: Event): void {
    const e = event as ErrorEvent;
    const info: ErrorInfo = {
      kind: 'error',
      message: e.message,
      stack: (e.error as Error | undefined)?.stack,
      source: e.filename,
      timestamp: Date.now(),
    };
    try {
      onFatal(info);
    } catch {
      /* swallow — caller bug; must not re-trigger the error pipeline */
    }
  }

  function handleRejection(event: Event): void {
    const e = event as PromiseRejectionEvent;
    const info: ErrorInfo = {
      kind: 'unhandledrejection',
      message: stringifyReason(e.reason),
      stack: (e.reason as Error | undefined)?.stack,
      timestamp: Date.now(),
    };
    try {
      onFatal(info);
    } catch {
      /* swallow — caller bug; must not re-trigger the error pipeline */
    }
  }

  target.addEventListener('error', handleError);
  target.addEventListener('unhandledrejection', handleRejection);

  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      target.removeEventListener('error', handleError);
      target.removeEventListener('unhandledrejection', handleRejection);
    },
  };
}
